import { IBaseApiModel, IFileAttachment, INoteEvent, IPhoneNumberMetadata, IUser } from '..';

export interface IPhoneCallCategory {
	archived?: boolean;
	hexColor?: string;
	id?: string;
	name: string;
	usage?: number;
}

export enum PhoneCallDirection {
	Outbound = 'Outbound',
	Inbound = 'Inbound',
}

export enum LeadCallMode {
	Normal = 'Normal',
	PracticeCall = 'PracticeCall',
}

export interface IStoredFile {
	accountId?: string;
	composedFilePath?: string;
	fileName?: string;
	publicUrl?: string;
	regionName?: string;
	resourceType?: string;
}

export interface ICardImageFile extends IFileAttachment {
	storedFile?: IStoredFile;
}

export enum PhoneCallRecordingTranscriptionStatus {
	AvailableForTranscription = 'AvailableForTranscription',
	RecordingValidationFailure = 'RecordingValidationFailure',
	TranscriptionComplete = 'TranscriptionComplete',
	TranscriptionFailure = 'TranscriptionFailure',
}

export type CoachingStepName = 'GetToDm' | 'Hook' | 'Pitch' | 'QualifyingQuestions' | 'Close';

/**
 * Full backend `SalesCallStep` taxonomy. `PostDemoQualify` is the 6th process area added by the
 * points-based rubric; `naAreas` and `suggestionEvaluations[].step` range over these values.
 */
export type SalesCallStep = CoachingStepName | 'Unknown' | 'Other' | 'PostDemoQualify';

export type CoachingSuggestionStepName = SalesCallStep;

export type CoachingClassification = 'followed' | 'partially_followed' | 'not_followed' | 'not_applicable';

export type CoachingSuggestionQuality = 'Unknown' | 'Sound' | 'Mistimed' | 'Irrelevant';

export type GamePlanPitchAdherence = 'NotApplicable' | 'Delivered' | 'PartiallyDelivered' | 'Ignored' | 'NoOpportunity';

export interface ICoachingStepEvaluation {
	step: CoachingStepName;
	classification: CoachingClassification;
}

export interface ICoachingSuggestionEvaluation {
	step: CoachingSuggestionStepName;
	classification: CoachingClassification;
	reasoning: string;
	quality?: CoachingSuggestionQuality;
}

export interface ICoachingGamePlanPitchResult {
	adherence: GamePlanPitchAdherence;
	reasoning?: string | null;
	repEvidence?: string | null;
}

/** Classified call type (attribution) — backend `ColdCallType`. */
export enum ColdCallType {
	Unknown = 'Unknown',
	FreshColdCall = 'FreshColdCall',
	WarmInbound = 'WarmInbound',
	ReEngagement = 'ReEngagement',
	FollowUp = 'FollowUp',
	RescheduleOrConfirmation = 'RescheduleOrConfirmation',
}

/** Single highest outcome achieved on the call — backend `ColdCallOutcomeTier`. */
export enum ColdCallOutcomeTier {
	Unknown = 'Unknown',
	BookedDemoPrequalified = 'BookedDemoPrequalified',
	BookedDemo = 'BookedDemo',
	FirmScheduledCallback = 'FirmScheduledCallback',
	ReferralOrTransferToDm = 'ReferralOrTransferToDm',
	CleanDisqualification = 'CleanDisqualification',
	ReachedDmAndAdvanced = 'ReachedDmAndAdvanced',
	NewContactChannelObtained = 'NewContactChannelObtained',
	OkToSendInfo = 'OkToSendInfo',
	NoAdvance = 'NoAdvance',
}

/**
 * Full detail behind a points-based rubric score (backend `CoachingRubricBreakdown`). Present only
 * on records scored by the new scorer — its presence is the discriminator between the new and
 * legacy {@link ICoachingAdherenceResult} shapes. The id arrays (`firedTriggers`,
 * `negativeModifiers`, `disqualifiers`, `flags`) hold snake_case ids.
 */
export interface ICoachingRubricBreakdown {
	callType: ColdCallType;
	/** How much NEW selling work happened (0–1); scales the follow-up / re-engagement outcome. */
	newWorkScore: number;
	outcomeTier: ColdCallOutcomeTier;
	/** Raw outcome points before the call-type attribution multiplier. */
	outcomeBase: number;
	outcomeMultiplier: number;
	/** Final outcome contribution, `round(outcomeBase × outcomeMultiplier)`, 0–30. */
	outcomePoints: number;
	processEarned: number;
	/** Max process points for applicable areas (70 minus N/A caps). */
	applicableProcessMax: number;
	/** Uncapped total of all fired trigger points — a tiebreaker, NOT the score. */
	effortRaw: number;
	rawFinalBeforeDisqualifiers: number;
	// Nullable fields are omitted from the response (backend `NullValueHandling.Ignore`), so they are
	// optional here and read null-safe (`?? []` / `?? 0`) by consumers.
	firedTriggers?: string[];
	negativeModifiers?: string[];
	disqualifiers?: string[];
	naAreas?: SalesCallStep[];
	/** A severe (Tier-A) disqualifier forced the score to 0. */
	tierADisqualifier: boolean;
	flags?: string[];
	/** Curation flag: a clean fresh-cold-call exemplar. */
	isColdExemplar: boolean;
	/** Classifier confidence (0–1); low for single-channel / truncated transcripts. */
	confidence?: number;
	/** Short supporting quotes — rep-only, topic-level. */
	evidence?: string;
	rationale?: string;
}

export interface ICoachingAdherenceResult {
	/**
	 * Legacy records: suggestion-adherence %. New records (`rubricBreakdown` present): rubric quality
	 * score 0–100 (70 process + 30 outcome). `-1` = not applicable (new: no transcript).
	 */
	score: number;
	/**
	 * Per-area score. New records: 0–100, `null` for an N/A area (no opportunity). Legacy records:
	 * flat 100/50/0/-1.
	 */
	getToDmScore?: number | null;
	hookScore?: number | null;
	pitchScore?: number | null;
	qualifyingQuestionsScore?: number | null;
	closeScore?: number | null;
	/** 6th per-area score (Post-Demo Qualify) — new records only; `null` unless a demo was booked. */
	postDemoQualifyScore?: number | null;
	evaluatedDate: string;
	/** Legacy 5-step evaluation; always `null` on new records. */
	stepEvaluations?: ICoachingStepEvaluation[] | null;
	/** Per-tip supporting feedback; `null` when the call had no coach suggestions or the eval failed. */
	suggestionEvaluations?: ICoachingSuggestionEvaluation[] | null;
	/** Supporting summary text; may be `null` on new records. */
	summary?: string | null;
	/** New rubric detail; `null` on legacy records. Discriminates the new vs. legacy result shape. */
	rubricBreakdown?: ICoachingRubricBreakdown | null;
	suggestionQualityScore?: number | null;
	suggestionAdherenceScore?: number | null;
	missedSoundSuggestions?: number | null;
	gamePlanPitch?: ICoachingGamePlanPitchResult | null;
}

/**
 * Lifecycle timestamps the backend stamps as a call progresses. The presence of `connected` is the
 * signal that the call actually answered/connected (vs. ringing out or going to voicemail).
 */
export interface IPhoneCallMilestones {
	calling?: string;
	connected?: string;
	hangup?: string;
	ringing?: string;
}

export interface IPhoneCall extends IBaseApiModel {
	aiConversationId?: string;
	callStatus?: string;
	categories?: IPhoneCallCategory[];
	/** Number of notes/comments logged against the call. */
	commentCount?: number;
	/** Call lifecycle timestamps; `milestones.connected` means the call connected. */
	milestones?: IPhoneCallMilestones;
	coachingAdherenceResult?: ICoachingAdherenceResult;
	companyId?: string;
	contactIds?: string[];
	creationDate?: string;
	creator?: { id: string };
	creatorPhoneNumber?: IPhoneNumberMetadata;
	direction?: PhoneCallDirection;
	duration?: number;
	endTime?: string;
	leadCallMode?: LeadCallMode;
	phoneNumber?: IPhoneNumberMetadata;
	recording?: IStoredFile;
	recordingChannels?: number;
	recordingTranscriptionStatus?: PhoneCallRecordingTranscriptionStatus;
	startTime?: string;
	transcriptionId?: string;
	/**
	 * UI version the rep was on when this call was created, stamped by the backend from the rep's
	 * `coffeePreferences.uiVersion` (e.g. `'focused-view'` vs. `'legacy'`). Lets call activity —
	 * including inbound calls — be attributed to a UI version for adoption reporting. Optional
	 * because older calls predate the stamping.
	 */
	uiVersion?: string;
	/**
	 * The AI agent template that actually ran coaching on this call, stamped at create/link time.
	 * Authoritative, unlike `agentTemplateName`. Absent on calls that predate the stamping until the
	 * backfill has run, and on calls with no coaching.
	 */
	agentTemplateId?: string;
	/**
	 * Display name for `agentTemplateId`, denormalized so lists need no per-row lookup. Best-effort:
	 * omitted when the template was deleted or is unreadable even though the id is present, so never
	 * render this without falling back to the id.
	 */
	agentTemplateName?: string;
}

export enum TranscriptionStatusFilter {
	TranscriptionComplete = 'TranscriptionComplete',
	TranscriptionAvailable = 'TranscriptionAvailable',
}

export enum DealStageOptions {
	AppointmentNoShow = 'Appointment No Show',
	AppointmentRescheduled = 'Appointment Rescheduled',
	AppointmentScheduled = 'Appointment Scheduled',
	ClosedLost = 'Closed Lost',
	ClosedWon = 'Closed Won',
}

export enum DemoStatusFilter {
	DemosToConfirm = 'DemosToConfirm',
	DemosToReschedule = 'DemosToReschedule',
	UpcomingDemos = 'UpcomingDemos',
	PastDemos = 'PastDemos',
}

export enum TranscriptionStatus {
	Failed = 'Failed',
	InProgress = 'InProgress',
	Queued = 'Queued',
	Completed = 'Completed',
	Unknown = 'Unknown',
	Available = 'Available',
}

export enum TranscriptionProvider {
	Amazon = 'Amazon',
}

export interface ITranscription extends IBaseApiModel {
	creationDate?: string;
	creator?: IUser;
	id?: string;
	lastModifiedDate?: string;
	numberOfChannels?: number;
	read?: boolean;
	status?: TranscriptionStatus;
	transcriptionProvider?: TranscriptionProvider;
	transcriptionProviderId?: string;
}

export interface ISpeakerLine {
	speaker: string;
	line: string;
	startTimeOffset: number;
	endTimeOffset: number;
}

export interface ITranscriptionSpeakerLines extends IBaseApiModel {
	speakerLines: ISpeakerLine[];
	transcriptionId?: string;
	transcriptionViewType?: string;
}

export interface IPhoneCallCompletedEvent extends INoteEvent {
	phoneCall?: IPhoneCall;
}
